// src/components/chatbot/index.tsx - UPDATED WITH VOICE INTEGRATION
import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

import { Message, SuggestionItem, ChatbotProps } from './types';
import ChatbotButton from './ChatbotButton';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ConfirmationModal from './ConfirmationModal';
import { getBotResponse, defaultSuggestions, saveMessages, loadMessages } from './chatbotService';
import { PUBLIC_OPENAI_API_KEY } from '@/config/env';

// Import API hooks
import { 
  useSendChatMessageMutation,
  useGetChatHistoryQuery,
  useClearChatHistoryMutation,
  useLazyGetChatHistoryQuery
} from '@/services/chatbotApi';

// Import Voice AI Workforce types
import { 
  VoiceCommand, 
  VoiceResponse, 
  VoiceAIConfig, 
  AIProvider, 
  SpeechProvider, 
  ResponseMode 
} from '@voice-ai-workforce/types';

// Voice mode type
type VoiceMode = 'voice-to-voice' | 'voice-to-text' | 'disabled';

export const Chatbot: React.FC<ChatbotProps> = ({
  initialMessage = "Hi there! I'm your Staffluent assistant. How can I help you today?",
  botAvatar = "",
  botName = "Staffluent Assistant",
  className,
  storageKey = "staffluent-chat-history",
  suggestions: initialSuggestions = [],
  onSendMessage,
  currentView,
  enableVoice = true,
  voiceConfig: providedVoiceConfig = {}
}) => {
  // Get credentials from localStorage
  const businessId = localStorage.getItem('businessId') || '';
  const userId = localStorage.getItem('osId');
  
  // State for the chatbot
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  // Pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Voice state
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  // @ts-ignore
  const [lastVoiceMode, setLastVoiceMode] = useState<VoiceMode>('disabled');
  
  // Reference to track current view
  const currentViewRef = useRef<string | null>(currentView || null);
  
  // Update current view ref when prop changes
  useEffect(() => {
    if (currentView) {
      currentViewRef.current = currentView;
    }
  }, [currentView]);

  // Voice AI Configuration
  const voiceConfig: VoiceAIConfig = {
    // Required business context
    businessContext: {
      name: "Staffluent",
      domain: "workforce_management",
      capabilities: ["time tracking", "task management", "team coordination", "project status"]
    },
    
    // AI Providers with new structure
    aiProviders: {
      primary: {
        provider: AIProvider.OPENAI,
        apiKey: PUBLIC_OPENAI_API_KEY || '',
        model: 'gpt-3.5-turbo'
      },
      fallbacks: [{
        provider: AIProvider.KEYWORDS,
        fallbackMode: true
      }]
    },
    
    // Speech configuration
    speechToText: {
      provider: SpeechProvider.WEB_SPEECH,
      language: 'en-US',
      continuous: false
    },
    textToSpeech: {
      provider: SpeechProvider.WEB_SPEECH,
      speed: 1.0
    },
    
    // Interface and response modes
    interfaceMode: 'end-user',
    responseMode: ResponseMode.BOTH,
    confidenceThreshold: 0.7,
    
    // Merge with provided config
    ...providedVoiceConfig
  };

  // API hooks
  const [sendChatMessage, { isLoading: isSending }] = useSendChatMessageMutation();
  const [clearHistory] = useClearChatHistoryMutation();
  const [fetchMoreMessages] = useLazyGetChatHistoryQuery();
  
  const { 
    data: historyData, 
    isLoading: isLoadingHistory 
  } = useGetChatHistoryQuery(
    { 
      businessId, 
      sessionId: sessionId || undefined,
      userId: userId || undefined,
      limit: 50,
      page: 1
    }, 
    { 
      skip: !businessId || !sessionId 
    }
  );
  
  const { toast } = useToast();
  
  // Load session ID from localStorage on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem(`${storageKey}-session-id`);
    if (savedSessionId) {
      setSessionId(savedSessionId);
    }
  }, [storageKey]);
  
  // Load messages from API or localStorage
  useEffect(() => {
    // If we have API history data, use it
    if (historyData?.success && historyData.messages.length > 0) {
      // Convert API messages to our format
      const convertedMessages = historyData.messages.map(msg => ({
        id: msg._id,
        content: msg.content,
        sender: msg.sender,
        timestamp: new Date(msg.createdAt),
        suggestions: msg.suggestions || [],
        metadata: {
          ...msg.metadata || {},
          voiceGenerated: msg.metadata?.voiceGenerated || false,
          voiceMode: msg.metadata?.voiceMode || 'disabled'
        },
      }))
      .reverse();
      
      setMessages(convertedMessages);
      setCurrentPage(1);
    } else {
      // Otherwise, try to load from localStorage
      const savedMessages = loadMessages(storageKey);
      if (savedMessages && savedMessages.length > 0) {
        try {
          const parsedMessages = savedMessages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }));
          
          setMessages(parsedMessages);
        } catch (error) {
          console.error('Failed to parse saved messages:', error);
          addInitialMessage();
        }
      } else {
        addInitialMessage();
      }
    }
  }, [historyData, storageKey, initialMessage, initialSuggestions]);
  
  // Load older messages function
  const loadOlderMessages = async () => {
    if (isLoadingMore || !businessId || !sessionId) return;
    
    setIsLoadingMore(true);
    try {
      const result = await fetchMoreMessages({
        businessId,
        sessionId,
        userId: userId || undefined,
        limit: 50,
        page: currentPage + 1
      });
      
      if ('data' in result && result.data?.success && result.data.messages.length > 0) {
        const olderMessages = result.data.messages.map(msg => ({
          id: msg._id,
          content: msg.content,
          sender: msg.sender,
          timestamp: new Date(msg.createdAt),
          suggestions: msg.suggestions || [],
          metadata: {
            ...msg.metadata || {},
            voiceGenerated: msg.metadata?.voiceGenerated || false,
            voiceMode: msg.metadata?.voiceMode || 'disabled'
          },
        }));
        
        setMessages(prev => [...olderMessages, ...prev]);
        setCurrentPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to load older messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };
  
  // Add initial message
  const addInitialMessage = () => {
    const initialMsg: Message = {
      id: '0',
      content: initialMessage,
      sender: 'bot',
      timestamp: new Date(),
      suggestions: initialSuggestions.length > 0 
        ? initialSuggestions 
        : defaultSuggestions,
    };
    
    setMessages([initialMsg]);
  };
  
  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(messages, storageKey);
    }
    
    // Save session ID to localStorage if it exists
    if (sessionId) {
      localStorage.setItem(`${storageKey}-session-id`, sessionId);
    }
  }, [messages, sessionId, storageKey]);
  
  // Update unread count
  useEffect(() => {
    if (!isOpen) {
      // Count bot messages that came after the last user message
      const lastUserMsgIndex = [...messages].reverse().findIndex(msg => msg.sender === 'user');
      
      if (lastUserMsgIndex === -1) {
        // No user messages yet, so count all bot messages
        setUnreadCount(messages.filter(msg => msg.sender === 'bot').length);
      } else {
        // Count bot messages after the last user message
        setUnreadCount(lastUserMsgIndex);
      }
    } else {
      // Clear unread count when chat is open
      setUnreadCount(0);
    }
  }, [isOpen, messages]);
  
  // Toggle the chat window
  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (isMinimized) {
      setIsMinimized(false);
    }
    if (!isOpen) {
      setUnreadCount(0);
    }
  };
  
  // Toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };
  
  // Handle sending a message (updated to handle voice)
  const handleSendMessage = async (messageContent?: string, voiceMode?: VoiceMode) => {
    const textToSend = messageContent || inputValue;
    if (!textToSend.trim()) return;
    
    // Store voice mode for the session
    if (voiceMode) {
      setLastVoiceMode(voiceMode);
    }
    
    // Add user message with voice metadata
    const userMessage: Message = {
      id: Date.now().toString(),
      content: textToSend,
      sender: 'user',
      timestamp: new Date(),
      metadata: {
        voiceGenerated: !!voiceMode && voiceMode !== 'disabled',
        voiceMode: voiceMode || 'disabled'
      }
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    
    // Set appropriate loading state
    if (voiceMode && voiceMode !== 'disabled') {
      setIsProcessingVoice(true);
    } else {
      setIsTyping(true);
    }
    
    try {
      let botResponse: {
        text: string;
        suggestions?: SuggestionItem[];
        metadata?: {
          sourceId?: string;
          knowledgeUsed?: boolean;
          responseSource?: string;
          [key: string]: any;
        };
        sessionId?: string;
      };
      
      // If onSendMessage prop is provided, use it to get the response
      if (onSendMessage) {
        botResponse = await onSendMessage(textToSend);
      } else if (businessId) {
        // Use API if businessId is available
        const contextData = {
          currentView: currentViewRef.current,
          previousMessages: messages.slice(-5).map(msg => ({
            content: msg.content,
            sender: msg.sender
          })),
          sessionData: {
            messageCount: messages.length,
            lastInteraction: new Date().toISOString()
          },
          voiceContext: {
            isVoiceGenerated: !!voiceMode && voiceMode !== 'disabled',
            voiceMode: voiceMode || 'disabled',
            expectsVoiceResponse: voiceMode === 'voice-to-voice'
          }
        };
        
        const result = await sendChatMessage({
          businessId,
          message: textToSend,
          userId: userId || undefined,
          sessionId: sessionId || undefined,
          context: contextData
        });
        
        // Access result data safely
        if ('data' in result && result.data) {
          botResponse = {
            text: result.data.text,
            suggestions: result.data.suggestions,
            sessionId: result.data.sessionId,
            metadata: {
              ...result.data.metadata,
              voiceContext: {
                shouldSpeak: voiceMode === 'voice-to-voice',
                voiceMode: voiceMode || 'disabled'
              }
            }
          };
          
          // Update session ID if one was provided
          if (result.data.sessionId && result.data.sessionId !== sessionId) {
            setSessionId(result.data.sessionId);
          }
        } else {
          throw new Error('Failed to get response from API');
        }
      } else {
        // Fallback to local response if no API
        const localResponse = getBotResponse(textToSend);
        
        if (typeof localResponse === 'string') {
          botResponse = { text: localResponse };
        } else {
          botResponse = localResponse;
        }
      }
      
      // Add bot message after a short delay
      setTimeout(() => {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: botResponse.text,
          sender: 'bot',
          timestamp: new Date(),
          suggestions: botResponse.suggestions || [],
          metadata: {
            ...botResponse.metadata,
            voiceContext: botResponse.metadata?.voiceContext,
            responseToVoice: !!voiceMode && voiceMode !== 'disabled'
          }
        };
        
        setMessages((prev) => {
          // Check if this exact same bot message was already added in the last few seconds
          const recentDuplicate = prev.find(msg => 
            msg.sender === 'bot' && 
            msg.content === botMessage.content &&
            new Date().getTime() - msg.timestamp.getTime() < 5000
          );
          
          if (recentDuplicate) {
            return prev; // Don't add the duplicate
          }
          
          return [...prev, botMessage];
        });
          
        setIsTyping(false);
        setIsProcessingVoice(false);

        // Handle voice-to-voice response
        if (voiceMode === 'voice-to-voice' && 'speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(botResponse.text);
          utterance.rate = 0.9;
          utterance.pitch = 1.0;
          speechSynthesis.speak(utterance);
        }
      }, 800 + Math.random() * 700);
    } catch (error) {
      // Handle error
      setTimeout(() => {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: "I'm sorry, I encountered an error while processing your request. Please try again.",
          sender: 'bot',
          timestamp: new Date(),
          isError: true,
        };
        
        setMessages((prev) => [...prev, errorMessage]);
        setIsTyping(false);
        setIsProcessingVoice(false);
      }, 1000);
      
      console.error('Error getting bot response:', error);
    }
  };
  
  // Handle voice command - UPDATED to handle VoiceCommand object
  const handleVoiceCommand = (command: VoiceCommand, mode: VoiceMode) => {
    // Extract text from VoiceCommand object
    const commandText = command.rawText || command.intent || 'voice input';
    
    // Add voice command as user message and process it
    handleSendMessage(commandText, mode);
    
    if (enableVoice) {
      toast({
        title: mode === 'voice-to-voice' ? "Voice conversation" : "Voice input received",
        description: `Processing: "${commandText}"`,
      });
    }
  };

  // Handle voice response - UPDATED to handle VoiceResponse object
  const handleVoiceResponse = (response: VoiceResponse) => {
    // Extract text from VoiceResponse object
    const responseText = response.text || 'voice response';
    
    // Log the response details for debugging
    console.log('Voice response received:', {
      text: responseText,
      canHandle: response.canHandle,
      shouldFallback: response.shouldFallback,
      commandType: response.commandType,
      confidence: response.confidence
    });

    // Handle fallback if needed
    if (response.shouldFallback && !response.canHandle) {
      toast({
        title: "Connecting to business system",
        description: "Getting more detailed information...",
      });
    }
  };
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SuggestionItem) => {
    if (suggestion.action) {
      suggestion.action();
    } else {
      setInputValue(suggestion.text);
      // Small delay to show the text in the input before sending
      setTimeout(() => {
        handleSendMessage();
      }, 100);
    }
  };
  
  // Open confirmation modal for clearing chat
  const handleOpenClearChatModal = () => {
    setShowClearConfirmation(true);
  };
  
  // Close confirmation modal
  const handleCloseClearChatModal = () => {
    setShowClearConfirmation(false);
  };
  
  // Clear chat history after confirmation
  const handleConfirmClearChat = async () => {
    // If we have a session ID and businessId, clear via API
    if (sessionId && businessId) {
      try {
        await clearHistory({ businessId, sessionId });
      } catch (error) {
        console.error('Failed to clear chat history via API:', error);
      }
    }
    
    // Clear local storage
    localStorage.removeItem(storageKey);
    localStorage.removeItem(`${storageKey}-session-id`);
    
    // Reset session ID
    setSessionId(null);
    
    // Reset pagination state
    setCurrentPage(1);
    setIsLoadingMore(false);
    
    // Add initial message
    addInitialMessage();
    setShowClearConfirmation(false);
    
    toast({
      title: "Chat history cleared",
      description: "Your conversation has been reset.",
    });
  };
  
  return (
    <>
      {/* Chat Button */}
      <ChatbotButton onClick={toggleChat} unreadCount={unreadCount} />
      
      {/* Chat Window */}
      {isOpen && (
        <Card
          className={cn(
            "fixed bottom-20 right-4 z-50 w-80 md:w-96 shadow-lg flex flex-col rounded-lg overflow-hidden transition-all duration-300",
            isMinimized ? "h-14" : "h-[500px] max-h-[80vh]",
            className
          )}
        >
          {/* Chat Header */}
          <ChatHeader
            botName={botName}
            botAvatar={botAvatar}
            isMinimized={isMinimized}
            onMinimize={toggleMinimize}
            onClose={toggleChat}
            onClearChat={handleOpenClearChatModal}
          />
          
          {!isMinimized && (
            <>
              {/* Messages Area */}
              <ChatMessages
                messages={messages}
                isTyping={isTyping || isSending || isLoadingHistory || isProcessingVoice}
                botAvatar={botAvatar}
                botName={botName}
                onSuggestionClick={handleSuggestionClick}
                // @ts-ignore
                onScrollToTop={loadOlderMessages}
                isLoadingMore={isLoadingMore}
                processingVoice={isProcessingVoice}
              />
              
              {/* Input Area with Voice Support */}
              <ChatInput
                value={inputValue}
                onChange={handleInputChange}
                onSend={() => handleSendMessage()}
                isDisabled={isTyping || isSending || isProcessingVoice}
                autoFocus={true}
                onVoiceCommand={handleVoiceCommand}
                onVoiceResponse={handleVoiceResponse}
                enableVoice={enableVoice}
                voiceConfig={voiceConfig}
              />
            </>
          )}
        </Card>
      )}
      
      {/* Confirmation Modal for Clear Chat */}
      <ConfirmationModal
        isOpen={showClearConfirmation}
        title="Clear Chat History"
        description="Are you sure you want to clear the entire chat history? This action cannot be undone."
        confirmText="Clear History"
        cancelText="Cancel"
        onConfirm={handleConfirmClearChat}
        onCancel={handleCloseClearChatModal}
        variant="destructive"
      />
    </>
  );
};

export default Chatbot;